import { Component, EventEmitter, Injector, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LazyLoadEvent } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs/operators';
import { AppComponentBase, PagedTableSummary } from 'src/app/app-component-base';

import { BusquedaPacientesGridComponent } from './busquedapacientes-grid.component';
import {
    BusquedaIMSSResultDto,
    BusquedaUrgenciasGetListQuery,
    CatalogosGeneralesServiceProxy,
    ComboboxItemDtoOfLong,
    ConfigurarHospitalServiceProxy,
    GetPatientLocalSearchListQuery,
    IntegracionIMSSServiceProxy,
    PatientGetByCURPQuery,
    PatientGetByIdQuery,
    PatientResponse,
    PatientsServiceProxy
} from '../../../../shared/service-proxies/service-proxies';
import { DateTimeService } from '../../../../shared/services/datetime.service';
import { BusquedaPacientesUrgenciasGridComponent } from './busquedapacientes-urgencias-grid.component';
import { BusquedaPacientesIMSSGridComponent } from './busquedapacientes-imss-grid.component';
import { Router } from '@angular/router';
import { AppConsts } from '../../../../shared/AppConsts';

@Component({
    selector: 'app-busqueda-pacientes',
    templateUrl: 'busquedapacientes.component.html'
})
export class BusquedaPacientesComponent extends AppComponentBase implements OnInit {

    // @Output() selectPatient: EventEmitter<PatientResponse> = new EventEmitter<PatientResponse>();
    // @Output() removePatient: EventEmitter<any> = new EventEmitter<any>();

    form: FormGroup;
    loading = false;
    blockedDocument = false;

    pacienteSeleccionado: PatientResponse;

    sexosList: ComboboxItemDtoOfLong[] = [];
    estatusList: ComboboxItemDtoOfLong[] = [];
    tiposPacientesList: ComboboxItemDtoOfLong[] = [];
    tiposPacientesIMSSList: ComboboxItemDtoOfLong[] = [];
    hospitalesList: ComboboxItemDtoOfLong[] = [];
    motivosList: ComboboxItemDtoOfLong[] = [];

    isHost = false;
    isModal = false;

    constructor(
        injector: Injector,
        private formBuilder: FormBuilder,
        private router: Router,
        private modalRef: DynamicDialogRef,
        private modalConfig: DynamicDialogConfig,
        private catalogosGeneralesService: CatalogosGeneralesServiceProxy,
        private patientService: PatientsServiceProxy,
        private integracionIMSSService: IntegracionIMSSServiceProxy,
        private confUnidadService: ConfigurarHospitalServiceProxy,
        private datetimeService: DateTimeService
    ) {
        super(injector);

        const self = this;
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.form.controls;
    }

    ngOnInit() {
        const self = this;

        self.isHost = AppConsts.isHost;

        self.isModal = self.modalConfig.data != null;

        self.prepareForm();

        self.getSexos();
        self.getEstatus();
        self.getTiposPacientes();
        self.getTiposPacientesIMSS();
        self.getMotivosIngresos();

        if (self.isHost) {
            self.getHospitales();
        } else {
            self.getHospitalUnidadList();
        }

        // Se suscribe al servicio de búsqueda de pacientes
        this.busquedaPacienteService.pacienteSeleccionado.subscribe(m => {
            self.pacienteSeleccionado = m;
            if (self.pacienteSeleccionado) {
                self.setPaciente(self.pacienteSeleccionado);
            } else {
                self.prepareForm();
                self.setTipoPacientesDefault();
            }
        });
    }

    setPaciente(pac: PatientResponse): void {
        const self = this;

        self.pacienteSeleccionado = pac;

        self.f.nombre.setValue(self.pacienteSeleccionado.fullName);
        self.f.nss.setValue(self.pacienteSeleccionado.nss);
        self.f.sexo.setValue(self.pacienteSeleccionado.sex);
        // self.f.estatus.setValue(self.pacienteSeleccionado.sta);
        self.f.tipoPaciente.setValue(self.pacienteSeleccionado.patientTypeImss);
        self.f.agregadoMedico.setValue(self.pacienteSeleccionado.medicalAttache);
        self.f.idee.setValue(self.pacienteSeleccionado.idee);
        // self.f.edad.setValue(self.pacienteSeleccionado.nss);
    }

    quitarPaciente(): void {
        const self = this;

        self.busquedaPacienteService.removePatient();
    }

    setTipoPacientesDefault(): void {
        const self = this;

        if (self.tiposPacientesList.length > 0) {
            self.f.tipoPaciente.setValue(self.tiposPacientesList[0].value);
        }

        if (self.tiposPacientesIMSSList.length > 0) {
            self.f.tipoPacienteIMSS.setValue(self.tiposPacientesIMSSList[0].value);
        }
    }

    buscarLocal(): void {
        const self = this;

        const filter = new GetPatientLocalSearchListQuery();
        filter.name = self.f.nombre.value;
        filter.nss = self.f.nss.value;
        filter.sex = self.f.sexo.value;
        filter.hospitalId = self.f.hospital.value;
        filter.statusId = self.f.estatus.value;
        filter.patientType = self.f.tipoPaciente.value;

        if (self.f.agregadoMedico.value) {
            filter.medicalAttache = self.f.agregadoMedico.value.toUpperCase();
        }

        filter.idee = self.f.idee.value;

        self.blockedDocument = true;

        self.patientService.getPatientsLocalSearchList(filter)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {

                if (data.totalCount <= 0) {
                    self.alertService.error(self.l('Busquedas.Pacientes.NoHayCoincidenciasError'));
                } else {
                    self.mostrarGridModal(filter);
                }
            });
    }

    buscarUrgencias(): void {
        const self = this;

        const filter = new BusquedaUrgenciasGetListQuery();
        filter.nombre = self.f.nombreIMSS.value;
        filter.apellidoPaterno = self.f.apellidoPaternoIMSS.value;
        filter.apellidoMaterno = self.f.apellidoMaternoIMSS.value;
        filter.nss = self.f.nssIMSS.value;
        filter.tipoPaciente = self.f.tipoPacienteIMSS.value;

        if (self.f.agregadoMedicoIMSS.value) {
            filter.agregadoMedico = self.f.agregadoMedicoIMSS.value.toUpperCase();
        }

        filter.hospitalId = self.f.hospitalIMSS.value;

        if (self.f.fechaNacimientoIMSS.value) {
            filter.fechaNacimiento = self.datetimeService.getDateTimeToSaveServer(self.f.fechaNacimientoIMSS.value);
        }

        filter.motivoIngreso = self.f.motivoIMSS.value;
        filter.idee = self.f.ideeIMSS.value;

        self.blockedDocument = true;

        self.integracionIMSSService.getPacientesPorUrgenciasIMSS(filter)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {

                if (data.length <= 0) {
                    self.alertService.error(self.l('Busquedas.Pacientes.NoHayCoincidenciasError'));
                } else {
                    self.mostrarGridUrgenciasModal(data);
                }
            });
    }

    buscarIMSS(): void {
        const self = this;

        const filter = new BusquedaUrgenciasGetListQuery();
        filter.nombre = self.f.nombreIMSS.value;
        filter.apellidoPaterno = self.f.apellidoPaternoIMSS.value;
        filter.apellidoMaterno = self.f.apellidoMaternoIMSS.value;
        filter.nss = self.f.nssIMSS.value;
        filter.tipoPaciente = self.f.tipoPacienteIMSS.value;

        if (self.f.agregadoMedicoIMSS.value) {
            filter.agregadoMedico = self.f.agregadoMedicoIMSS.value.toUpperCase();
        }

        if (self.f.fechaNacimientoIMSS.value) {
            filter.fechaNacimiento = self.datetimeService.getDateTimeToSaveServer(self.f.fechaNacimientoIMSS.value);
        }

        filter.motivoIngreso = self.f.motivoIMSS.value;
        filter.idee = self.f.ideeIMSS.value;
        filter.hospitalId = self.f.hospitalIMSS.value;

        self.blockedDocument = true;

        self.integracionIMSSService.getPacientesPorIMSS(filter)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {

                if (data.valido) {
                    const msgs = data.mensajes.join('<br/>');
                    self.alertService.info(msgs);
                }
            });
    }

    getPacientePorId(pacienteId: number): void {
        const self = this;

        const req = new PatientGetByIdQuery();
        req.id = pacienteId;

        self.blockedDocument = true;

        self.patientService.getPatientById(req)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                // Se almacena en localStorage el paciente seleccionado
                self.busquedaPacienteService.setPatient(data);
                self.return();
            });
    }

    mostrarGridModal(filter: GetPatientLocalSearchListQuery): void {
        const self = this;
        const ref = self.dialogService.open(BusquedaPacientesGridComponent, {
            styleClass: 'd-sm d-md-70 d-lg-50 d-xl-50',
            header: self.l('Busquedas.Pacientes.Grid.Titulo'),
            showHeader: true,
            dismissableMask: false,
            // footer: 'DUMMY',
            data: {
                query: filter
            }
        });

        ref.onClose.subscribe((pacienteId: number) => {
            if (pacienteId) {
                self.getPacientePorId(pacienteId);
            }
        });
    }

    mostrarGridUrgenciasModal(data: BusquedaIMSSResultDto[]): void {
        const self = this;
        const ref = self.dialogService.open(BusquedaPacientesUrgenciasGridComponent, {
            styleClass: 'd-sm d-md-70 d-lg-70 d-xl-70',
            header: self.l('Busquedas.Pacientes.Grid.Titulo'),
            showHeader: true,
            dismissableMask: false,
            // footer: 'DUMMY',
            data: {
                data: data
            }
        });

        ref.onClose.subscribe((pacienteDto: BusquedaIMSSResultDto) => {
            if (pacienteDto) {
                self.buscarPacientePorCURP(pacienteDto);
            }
        });
    }

    mostrarGridIMSSModal(data: BusquedaIMSSResultDto[]): void {
        const self = this;
        const ref = self.dialogService.open(BusquedaPacientesIMSSGridComponent, {
            styleClass: 'd-sm d-md-70 d-lg-70 d-xl-70',
            header: self.l('Busquedas.Pacientes.Grid.Titulo'),
            showHeader: true,
            dismissableMask: false,
            // footer: 'DUMMY',
            data: {
                data: data
            }
        });

        ref.onClose.subscribe((pacienteDto: BusquedaIMSSResultDto) => {
            if (pacienteDto) {
                self.buscarPacientePorCURP(pacienteDto);
            }
        });
    }

    buscarPacientePorCURP(p: BusquedaIMSSResultDto): void {
        const self = this;

        if (!p.curp) {
            self.alertService.confirm(self.l('Catalogos.Patients.CreateNewRecordConfirm'), (si) => {
                if (si) {
                    self.browserStorageService.set(AppConsts.localStoragePatientKey, p);

                    // Si la pantalla está abierta en una ventana modal, entonces debe cerrarse
                    self.return();

                    // Enviar a la pantalla de nuevo paciente
                    self.router.navigate(['/app/catalogs/patients/create']);
                }
            });

            // Se detiene la ejecución
            return;
        }

        const req = new PatientGetByCURPQuery();
        req.curp = p.curp;

        self.patientService.getPatientByCURP(req)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                if (data) {
                    // Se almacena en localStorage el paciente seleccionado
                    self.busquedaPacienteService.setPatient(data);
                    self.return();
                } else {

                    self.alertService.confirm(self.l('Catalogos.Patients.CreateNewRecordConfirm'), (si) => {
                        if (si) {
                            self.browserStorageService.set(AppConsts.localStoragePatientKey, p);

                            // Si la pantalla está abierta en una ventana modal, entonces debe cerrarse
                            self.return();

                            // Enviar a la pantalla de nuevo paciente
                            self.router.navigate(['/app/catalogs/patients/create']);
                        }
                    });
                }
            });
    }

    getHospitales(): void {
        const self = this;

        self.blockedDocument = true;

        self.catalogosGeneralesService.getHospitalesForCombobox(null)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.hospitalesList = data;
            });
    }

    getHospitalUnidadList(): void {
        const self = this;

        self.confUnidadService.getHospitalesConfiguradosForCombobox()
            .pipe(finalize(() => {
            }))
            .subscribe(data => {
                self.hospitalesList = data;
            });
    }

    getSexos(): void {
        const self = this;

        self.blockedDocument = true;

        self.catalogosGeneralesService.getSexosForCombobox()
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.sexosList = data;
            });
    }

    getEstatus(): void {
        const self = this;

        self.blockedDocument = true;

        self.catalogosGeneralesService.getEstatusForCombobox()
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.estatusList = data;
            });
    }

    getTiposPacientes(): void {
        const self = this;

        self.blockedDocument = true;

        self.catalogosGeneralesService.getTiposPacientesIMSSForCombobox()
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.tiposPacientesList = data;

                self.setTipoPacientesDefault();
            });
    }

    getTiposPacientesIMSS(): void {
        const self = this;

        self.blockedDocument = true;

        self.catalogosGeneralesService.getTiposPacientesIMSSConClaveIMSSForCombobox()
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.tiposPacientesIMSSList = data;

                self.setTipoPacientesDefault();
            });
    }

    getMotivosIngresos(): void {
        const self = this;

        self.blockedDocument = true;

        self.catalogosGeneralesService.getMotivosIngresoForCombobox()
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.motivosList = data;
            });
    }

    prepareForm(): void {
        const self = this;

        self.form = self.formBuilder.group({
            nombre: [null],
            nss: [null],
            sexo: [null],
            hospital: [null],
            estatus: [null],
            tipoPaciente: [null],
            agregadoMedico: [null],
            idee: [null],
            edad: [null],
            tipoPacienteIMSS: [null],
            nssIMSS: [null],
            nombreIMSS: [null],
            apellidoPaternoIMSS: [null],
            apellidoMaternoIMSS: [null],
            fechaNacimientoIMSS: [null],
            hospitalIMSS: [null],
            motivoIMSS: [null],
            agregadoMedicoIMSS: [null],
            ideeIMSS: [null],
        });
    }

    return(): void {
        const self = this;

        if (self.isModal) {
            self.modalRef.close();
        }
    }
}
